import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { IconButton } from '@/lib/Buttons';
import { RefreshIcon } from '@/lib/Icons';
import { Audience } from '@/models/audiences';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useEvaluateAudienceMutation } from '@/services/endpoints/audiences';
import { useGetDistributionChannelsQuery } from '@/services/endpoints/distributionChannels';
import { useGetConnectorFlowInstancesQuery } from '@/services/endpoints/workspace-connectors';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AudienceDestinationTable } from './AudienceDestinationTable';
import { useCompanyValues } from '@/providers';

type AudienceDetailsDestinationsProps = {
  audience?: Audience;
};

export const AudienceDetailsDestinations: FC<AudienceDetailsDestinationsProps> = ({ audience }) => {
  const workspace = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isWorkspaceAdmin = useWorkspaceAdmin();
  const [reloadAnimation, setReloadAnimation] = useState(false);

  const { data: destinations, isLoading: isDestinationsLoading } = useGetDistributionChannelsQuery(
    {
      workspaceId: workspace.id,
      audienceIds: [audience?.id],
    },
    {
      skip: !audience?.id,
    }
  );

  const { data: flowInstances, isLoading: isFlowInstancesLoading } = useGetConnectorFlowInstancesQuery(
    {
      workspaceId: workspace.id,
      audienceId: audience?.id || '',
      lang,
    },
    {
      skip: !audience?.id,
    }
  );

  const [evaluate, { isLoading: isEvaluating }] = useEvaluateAudienceMutation();

  const handleSyncClick = () => {
    if (audience) {
      setReloadAnimation(true);
      setTimeout(() => setReloadAnimation(false), 2000);

      evaluate({ workspaceId: workspace.id, audienceId: audience.id });
      dispatch(
        showToast({
          type: ToastType.INFO,
          title: t('audiences:evaluate_title'),
          message: t('audiences:evaluate'),
        })
      );
    }
  };

  const distributionChannels = destinations || [];
  const flowInstancesList = flowInstances || [];
  const isLoading = isDestinationsLoading || isFlowInstancesLoading;
  const hasAnyConnections = distributionChannels.length > 0 || flowInstancesList.length > 0;

  return (
    <div>
      <div className='mt-8 flex items-start justify-between gap-8 border-b border-secondary-border pb-10'>
        <div className='flex w-[280px] shrink-0 flex-col gap-2'>
          <h3 className='text-lg font-semibold text-secondary-700'>{t('audience_details:destinations.title')}</h3>
          <p className='text-sm text-tertiary-600'>
            <Trans
              i18nKey={'audience_details:destinations.call_to_action_subtitle'}
              components={{
                newLine: <br />,
              }}
            />
          </p>
          <IconButton
            intent='secondary'
            onClick={handleSyncClick}
            disabled={!isWorkspaceAdmin || !hasAnyConnections}
            leadingIcon={<RefreshIcon className={`size-4 ${(reloadAnimation || isEvaluating) && 'animate-spin'}`} />}
            text={t('audience_edit:sync')}
          />
        </div>
        <AudienceDestinationTable audience={audience} destinations={distributionChannels} flowInstances={flowInstancesList} isLoading={isLoading} />
      </div>
    </div>
  );
};
